import type {
	IAuthenticationService,
	AuthenticationResult,
	TokenValidationResult,
} from "@/application/ports/services";
import type { IAuthRepository } from "@/application/ports/repositories";
import type { ILogger } from "@/application/ports/ILogger";
import { getAuthRepository } from "@/infrastructure/di/repositories";
import {
	getLocalStorageService,
	getLogger,
} from "@/infrastructure/di/services";
import { STORAGE_KEYS } from "@/infrastructure/storage/constants";

export class AuthenticationService implements IAuthenticationService {
	private authRepository: IAuthRepository;
	private logger: ILogger;
	private refreshTokenValue: string | null = null;

	constructor() {
		this.authRepository = getAuthRepository();
		this.logger = getLogger();
	}

	async login(email: string, password: string): Promise<AuthenticationResult> {
		try {
			const { tokens, user } = await this.authRepository.login(email, password);

			// Store refresh token for later use
			this.refreshTokenValue = tokens.refreshToken;
			this.storeRefreshToken(tokens.refreshToken);

			return {
				success: true,
				user,
				token: tokens.accessToken,
				refreshToken: tokens.refreshToken,
				expiresIn: tokens.expiresIn,
			};
		} catch (error) {
			this.logger.error("AuthService: Login error:", error);

			return {
				success: false,
				error:
					error instanceof Error ? error.message : "Invalid email or password",
			};
		}
	}

	async logout(): Promise<void> {
		try {
			const refreshToken = this.getStoredRefreshToken();

			if (refreshToken) {
				await this.authRepository.logout(refreshToken);
			}
		} catch (error) {
			this.logger.error("AuthService: Logout error:", error);
			// Don't throw here - logout should always succeed locally
		} finally {
			// Always clear local tokens
			this.refreshTokenValue = null;
			this.removeStoredRefreshToken();
		}
	}

	async validateToken(token: string): Promise<TokenValidationResult> {
		try {
			if (!token) {
				return {
					isValid: false,
					error: "No valid token provided",
				};
			}

			// Get user profile using the token
			const user = await this.authRepository.getUserProfile(token);

			return {
				isValid: true,
				user,
			};
		} catch (error) {
			this.logger.error("AuthService: Token validation error:", error);

			// Handle different error types
			if (error instanceof Error && "status" in error) {
				const apiError = error as Error & { status: number };
				if (apiError.status === 401) {
					return {
						isValid: false,
						error: "Token expired or invalid",
						errorCode: "token_expired",
					};
				}
			}

			// 500, network error, timeout — transient failure
			return {
				isValid: false,
				error:
					error instanceof Error ? error.message : "Token validation failed",
				errorCode: "transient_error",
			};
		}
	}

	async refreshToken(): Promise<AuthenticationResult> {
		const storedRefreshToken = this.getStoredRefreshToken();

		if (!storedRefreshToken) {
			return {
				success: false,
				error: "No refresh token available",
				errorCode: "no_refresh_token",
			};
		}

		// Step 1: Refresh the token — handle auth vs transient failures separately
		let tokens: {
			accessToken: string;
			refreshToken: string;
			expiresIn: number;
		};
		try {
			tokens = await this.authRepository.refreshToken(storedRefreshToken);
		} catch (error) {
			this.logger.error("AuthService: Token refresh error:", error);

			if (error instanceof Error && "status" in error) {
				const apiError = error as Error & { status: number };
				if (apiError.status === 401) {
					// Token was rejected — clear it
					this.refreshTokenValue = null;
					this.removeStoredRefreshToken();
					return {
						success: false,
						error: "Refresh token expired, please login again",
						errorCode: "refresh_token_rejected",
					};
				}
			}

			// 500, network error, timeout — do NOT clear the refresh token
			return {
				success: false,
				error: error instanceof Error ? error.message : "Token refresh failed",
				errorCode: "transient_error",
			};
		}

		// Step 2: Store new tokens immediately (refresh succeeded)
		this.refreshTokenValue = tokens.refreshToken;
		this.storeRefreshToken(tokens.refreshToken);

		// Step 3: Fetch user profile — failure here must NOT clear tokens
		let user;
		try {
			user = await this.authRepository.getUserProfile(tokens.accessToken);
		} catch (error) {
			this.logger.warn(
				"AuthService: getUserProfile failed after refresh, tokens still valid:",
				error,
			);
		}

		return {
			success: true,
			user,
			token: tokens.accessToken,
			refreshToken: tokens.refreshToken,
			expiresIn: tokens.expiresIn,
		};
	}

	async forgotPassword(
		email: string,
	): Promise<{ success: boolean; error?: string }> {
		try {
			await this.authRepository.forgotPassword(email);

			return {
				success: true,
			};
		} catch (error) {
			this.logger.error("AuthService: Forgot password error:", error);

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to send password reset email",
			};
		}
	}

	async resetPassword(
		token: string,
		newPassword: string,
	): Promise<{ success: boolean; error?: string }> {
		try {
			await this.authRepository.resetPassword(token, newPassword);

			return {
				success: true,
			};
		} catch (error) {
			this.logger.error("AuthService: Reset password error:", error);

			return {
				success: false,
				error: this.mapResetPasswordError(error),
			};
		}
	}

	private mapResetPasswordError(error: unknown): string {
		if (error instanceof Error && "status" in error) {
			const apiError = error as Error & { status: number };
			switch (apiError.status) {
				case 400:
				case 401:
					return "Invalid or expired reset token";
				case 404:
					return "Reset token not found";
				case 422:
					return "Password does not meet requirements";
				default:
					return "Failed to reset password";
			}
		}
		return "Failed to reset password";
	}

	private storeRefreshToken(refreshToken: string): void {
		try {
			getLocalStorageService().set(
				STORAGE_KEYS.AUTH_REFRESH_TOKEN,
				refreshToken,
			);
		} catch (error) {
			this.logger.error("Failed to store refresh token:", error);
		}
	}

	private getStoredRefreshToken(): string | null {
		try {
			return (
				this.refreshTokenValue ||
				getLocalStorageService().get<string>(STORAGE_KEYS.AUTH_REFRESH_TOKEN)
			);
		} catch (error) {
			this.logger.error("Failed to get stored refresh token:", error);
			return null;
		}
	}

	private removeStoredRefreshToken(): void {
		try {
			getLocalStorageService().remove(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
		} catch (error) {
			this.logger.error("Failed to remove stored refresh token:", error);
		}
	}
}
